// Veri Girişi sayfası — /clients/kpi/tablo (M16 T10, mockup 02). Toolbar: arama, kategori filtresi,
// doluluk sayacı (countSetFillStatus — tek kaynak), "Tümünü aç/kapat", görünüm (Yıllar/İşletmeler —
// kural 4, yalnız grup müşteride), Excel'e Aktar (T22 — KpiExportButton). Grid: KpiGrid; autosave
// kpi-store.saveCell (optimistik + rollback); kaydedilemeyen hücre sayacı toolbar'da kalıcı.
// Düzen (2026-10-07): KpiScreen `fullHeight` → kart flex sütun; toolbar/bantlar sabit, KpiGrid kabı
// kalan alanı doldurup kendi içinde kaydırır, alt bilgi satırı tablonun altında sabit.
// K9 (≤768): görünüm/işletme grubu ayrı satıra sarar, seçici `max-width:100%`.
// TODO-KPI-06: ≤1024 px'te arama kutusu dar (160), toolbar `flex-wrap` ile iki satıra sarar; grid
// kendi kabında yatay kaydırır (sayfa taşmaz). Toplu yapıştırma `saveCells` (tek refresh).
// Kırılım (spec §6.2): toplam bekleme seçicisi (`pendingCells`) + [Seti düzenle] / [Kırılımı bu sete
// ekle] → Veri Yönetimi (Task 14 modalı bağlanana kadar); alt bilgi gösterge sayısı metrik tanımıyla.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KpiScreen } from "../../components/kpi/KpiScreen";
import { KpiGrid } from "../../components/kpi/KpiGrid";
import { KpiFootnoteList } from "../../components/kpi/KpiFootnoteList";
import { KpiFootnoteContext, useGridFootnotes } from "../../lib/kpi-footnote-context";
import { KpiExportButton } from "../../components/kpi/KpiExportButton";
import { applyFormToKpi, listKpiSetFormMismatches } from "../../api/kpi-checks";
import type { KpiFormMismatch } from "@mahir/shared";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { LoadingState } from "../../components/common/StateSurface";
import { useKpiNoteSummary } from "../../hooks/use-kpi-note-summary";
import {
  isMetricIndicator,
  leafEntities,
  initialCollapsedIds,
  rootCategoryRows,
  type GridColumn,
  type GridViewMode,
} from "../../lib/kpi-grid-nav";
import { type GridNotes } from "../../lib/kpi-notes-ui";
import { formatKpiCell, KPI_COPY, kpiSetManagementHref } from "../../lib/kpi-ui";
import { codeForMetricId, readMetricIdParam } from "../../lib/kpi-unit-links";
import { useMediaQuery } from "../../lib/use-media-query";
import { useAuthStore } from "../../stores/auth-store";
import { selectTotalRecomputing, useKpiStore } from "../../stores/kpi-store";
import { useCreateSetNav } from "./use-create-set-nav";

const btn: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: "0.82rem",
  color: "var(--color-text)",
  minHeight: 34,
};

export function KpiTablePage(): JSX.Element {
  const createSet = useCreateSetNav();
  const grid = useKpiStore((s) => s.grid);
  const setId = useKpiStore((s) => s.setId);
  const clientId = useKpiStore((s) => s.clientId);
  // Finar rev-A 4 — "Veri Setine git" yalnız Veri Yönetimi menüsünü görenlere (kpi.manage)
  const canManageSets = useAuthStore((s) => s.user?.permissions["kpi.manage"] === true);
  const fill = useKpiStore((s) => s.fill);
  const cycle = useKpiStore((s) => s.cycle);
  const gridLoading = useKpiStore((s) => s.gridLoading);
  const years = useKpiStore((s) => s.years);
  const saveErrors = useKpiStore((s) => s.saveErrors);
  const lastSavedAt = useKpiStore((s) => s.lastSavedAt);
  const pendingSaves = useKpiStore((s) => s.pendingSaves);
  const pendingCells = useKpiStore((s) => s.pendingCells);
  const navigate = useNavigate();
  // DR4 — üye kaydı sürerken toplam hücresi bekler (grid yenilenene kadar)
  const isRecomputing = useCallback(
    (id: string, col: GridColumn) => selectTotalRecomputing(id, col)({ grid, pendingCells }),
    [grid, pendingCells],
  );
  // güvenlik/UX taraması 2026-10-08 (U-M1): 409 stale-write diyaloğu (portal kalıbı, sunucudaki değer)
  const staleConflict = useKpiStore((s) => s.staleConflict);
  const staleValueText = staleConflict?.current
    ? formatKpiCell(
        staleConflict.current.numericValue !== null
          ? { status: "filled", value: staleConflict.current.numericValue, isDerived: false }
          : staleConflict.current.textValue
            ? { status: "filled", text: staleConflict.current.textValue, isDerived: false }
            : undefined,
      ).text
    : null;
  // Dalga 3 — hücre notu rozetleri (iç yüz): görünür yıllar özeti, değer kaydı/not sonrası tazelenir
  const noteSummary = useKpiNoteSummary("internal", setId, years, lastSavedAt);
  const notes: GridNotes | undefined = setId
    ? { audience: "internal", setId, summary: noteSummary.summary, onChanged: noteSummary.reload }
    : undefined;
  // T21 — sete bağlı projelerde form ↔ KPI uyuşmazlıkları (FORM rozeti); grid yenilenince tazelenir
  const [formMismatches, setFormMismatches] = useState<KpiFormMismatch[]>([]);
  useEffect(() => {
    let alive = true;
    if (!setId) {
      setFormMismatches([]);
      return;
    }
    listKpiSetFormMismatches(setId)
      .then((m) => alive && setFormMismatches(m))
      .catch(() => alive && setFormMismatches([]));
    return () => {
      alive = false;
    };
  }, [setId, grid]);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [mode, setMode] = useState<GridViewMode>("years");
  const tablet = useMediaQuery("(max-width: 1024px)");
  const narrow = useMediaQuery("(max-width: 768px)");
  // K27 — çok işletmeli müşteride "Yıllar" görünümünün işletmesi (null = konsolide, salt okunur);
  // varsayılan: isDefault yaprak, yoksa ilk yaprak — giriş yapılabilsin diye konsolide varsayılmaz.
  const leaves = useMemo(() => (grid ? leafEntities(grid) : []), [grid]);
  const footnotes = useGridFootnotes(grid);
  const [yearsEntityId, setYearsEntityId] = useState<string | null>(null);
  useEffect(() => {
    if (leaves.length <= 1) return;
    if (yearsEntityId && leaves.some((l) => l.id === yearsEntityId)) return;
    if (yearsEntityId === "") return; // açıkça konsolide seçildi
    setYearsEntityId((leaves.find((l) => l.isDefault) ?? leaves[0])!.id);
  }, [leaves, yearsEntityId]);
  const [entityYear, setEntityYear] = useState<number>(
    years[years.length - 1] ?? new Date().getFullYear(),
  );
  // D6 — kategori bantları varsayılan KAPALI
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [initializedFor, setInitializedFor] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  // TODO-KPI-96: birim engel bağlantısı `?metricId=` taşır → ızgaradaki göstergenin koduna çevrilip
  // aynı highlight akışına (filtreleri temizle, kategoriyi aç, vurgula) bağlanır
  const metricIdParam = readMetricIdParam(`?${params.toString()}`);
  const highlight =
    params.get("highlight") ?? codeForMetricId(grid?.indicators ?? [], metricIdParam);

  // kökteki kırılımlı toplam kategori değil (spec §6.1 — shared effectiveNodeKind)
  const categories = useMemo(() => (grid ? rootCategoryRows(grid.indicators) : []), [grid]);
  const isGroup =
    (grid?.entities.filter((e) => e.isActive && !grid.entities.some((c) => c.parentId === e.id))
      .length ?? 0) > 1;

  useEffect(() => {
    if (!grid || initializedFor === grid.setId) return;
    setCollapsed(initialCollapsedIds(grid.indicators));
    setInitializedFor(grid.setId);
    setCategoryId(null);
  }, [grid, initializedFor]);

  useEffect(() => {
    if (!years.includes(entityYear)) setEntityYear(years[years.length - 1] ?? entityYear);
  }, [years, entityYear]);

  // kural 12 — highlight hedefini kapsayacak şekilde filtreleri temizle, kategoriyi aç
  useEffect(() => {
    if (!highlight || !grid) return;
    const target = grid.indicators.find((i) => i.code === highlight);
    if (!target) return;
    setSearch("");
    setCategoryId(null);
    setCollapsed((prev) => {
      const next = new Set(prev);
      let cur = target.parentId;
      while (cur) {
        next.delete(cur);
        cur = grid.indicators.find((i) => i.id === cur)?.parentId ?? null;
      }
      return next;
    });
    const t = setTimeout(() => {
      params.delete("highlight");
      params.delete("metricId");
      setParams(params, { replace: true });
    }, 2500);
    return () => clearTimeout(t);
  }, [highlight, grid?.setId]);

  const failedCount = Object.keys(saveErrors).length;
  const allOpen = grid ? collapsed.size === 0 : false;

  return (
    <KpiScreen
      title={KPI_COPY.titles.tablo}
      showYears
      fullHeight
      onCreateSet={() => createSet()}
      onCreateSetForClient={(id) => createSet(id)}
      {...(setId && canManageSets
        ? {
            right: (
              <Link
                to={kpiSetManagementHref(clientId, setId)}
                className="inline-flex items-center font-semibold"
                style={{ ...btn, textDecoration: "none" }}
                data-testid="kpi-go-to-set"
              >
                {KPI_COPY.goToSet}
              </Link>
            ),
          }
        : {})}
    >
      {!grid ? (
        <LoadingState label={KPI_COPY.grid.loading} />
      ) : (
        <div
          className="rounded-xl"
          data-testid="kpi-table-card"
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            className="flex items-center gap-2 flex-wrap px-3 py-2"
            style={{ borderBottom: "1px solid var(--color-border)", flexShrink: 0 }}
          >
            <input
              type="search"
              aria-label={KPI_COPY.grid.search}
              placeholder={KPI_COPY.grid.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...btn, width: tablet ? 160 : 220 }}
            />
            <select
              aria-label={KPI_COPY.grid.categoryLabel}
              style={btn}
              value={categoryId ?? ""}
              onChange={(e) => setCategoryId(e.target.value || null)}
            >
              <option value="">{KPI_COPY.grid.categoryAll}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            {fill ? (
              <span
                className="text-xs px-2 py-1 rounded-full"
                style={{ background: "var(--color-bg)", color: "var(--color-text-2)" }}
                aria-live="polite"
              >
                {/* P3-18: tek birim — bu sayaç HÜCRE sayar (yıl aralığı × işletme), gösterge değil */}
                {KPI_COPY.grid.fillCounter(fill)}
              </span>
            ) : null}
            <button
              type="button"
              style={btn}
              onClick={() =>
                setCollapsed(
                  allOpen
                    ? new Set(
                        grid.indicators
                          .filter((i) => grid.indicators.some((c) => c.parentId === i.id))
                          .map((i) => i.id),
                      )
                    : new Set(),
                )
              }
            >
              {allOpen ? KPI_COPY.grid.collapseAll : KPI_COPY.grid.expandAll}
            </button>
            {isGroup ? (
              <div
                role="group"
                aria-label={KPI_COPY.grid.viewLabel}
                className="flex items-center gap-1 flex-wrap text-xs"
                // K9 (768 px): görünüm grubu kendi satırında; işletme seçici kabın dışına taşmaz
                style={narrow ? { flexBasis: "100%", maxWidth: "100%" } : undefined}
              >
                <span style={{ color: "var(--color-text-3)" }}>{KPI_COPY.grid.viewLabel}:</span>
                {(["years", "entities"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={mode === m}
                    onClick={() => setMode(m)}
                    style={{
                      ...btn,
                      background: mode === m ? "var(--color-primary)" : "var(--color-surface)",
                      color: mode === m ? "var(--color-surface)" : "var(--color-text)",
                    }}
                  >
                    {m === "years" ? KPI_COPY.grid.viewYears : KPI_COPY.grid.viewEntities}
                  </button>
                ))}
                {mode === "years" ? (
                  <label
                    className="flex items-center gap-1"
                    style={{
                      color: "var(--color-text-3)",
                      ...(narrow ? { flex: "1 1 100%", minWidth: 0 } : {}),
                    }}
                  >
                    {KPI_COPY.yearsEntityLabel}
                    <select
                      aria-label={KPI_COPY.yearsEntityLabel}
                      data-testid="kpi-years-entity"
                      style={{ ...btn, minWidth: 0, maxWidth: "100%" }}
                      value={yearsEntityId ?? ""}
                      onChange={(e) =>
                        setYearsEntityId(e.target.value === "" ? "" : e.target.value)
                      }
                    >
                      {leaves.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                      <option value="">{KPI_COPY.yearsEntityConsolidated}</option>
                    </select>
                  </label>
                ) : null}
                {mode === "entities" ? (
                  <select
                    aria-label={KPI_COPY.grid.yearLabel}
                    style={btn}
                    value={entityYear}
                    onChange={(e) => setEntityYear(Number(e.target.value))}
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            ) : null}
            <span style={{ flex: 1 }} />
            {failedCount > 0 ? (
              // güvenlik/UX taraması 2026-10-08 (U-H3): durum metni role=alert'te, "Tekrar dene"
              // yazılan girdiyi yeniden gönderir, "Sunucudaki değerleri yükle" yazılanı bırakır.
              <span className="inline-flex items-center gap-2 text-xs">
                <span role="alert" style={{ color: "var(--color-error)", fontWeight: 600 }}>
                  {KPI_COPY.grid.failedCells(failedCount)}
                </span>
                <button
                  type="button"
                  style={{ ...btn, borderColor: "var(--color-error)", color: "var(--color-error)" }}
                  disabled={pendingSaves > 0}
                  onClick={() => void useKpiStore.getState().retryFailed()}
                >
                  {KPI_COPY.grid.retryFailed}
                </button>
                <button
                  type="button"
                  style={{ ...btn, color: "var(--color-text-2)" }}
                  disabled={pendingSaves > 0}
                  onClick={() => void useKpiStore.getState().discardFailed()}
                >
                  {KPI_COPY.grid.loadServerValues}
                </button>
              </span>
            ) : pendingSaves > 0 ? (
              <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                {KPI_COPY.grid.saving}
              </span>
            ) : lastSavedAt ? (
              <span className="text-xs" style={{ color: "var(--color-success)" }}>
                {KPI_COPY.grid.saved}
              </span>
            ) : null}
            <KpiExportButton setId={grid?.setId ?? null} years={years} langSelect />
          </div>
          {cycle ? (
            <div
              role="alert"
              className="text-xs px-3 py-2"
              style={{
                background: "var(--color-warning-bg)",
                color: "var(--color-warning-text)",
                borderBottom: "1px solid var(--color-warning-border)",
              }}
            >
              {KPI_COPY.grid.cycle(cycle.join(" → "))}
            </div>
          ) : null}
          {gridLoading ? (
            <div className="text-xs px-3 py-1" style={{ color: "var(--color-text-3)" }}>
              {KPI_COPY.grid.refreshing}
            </div>
          ) : null}
          <KpiFootnoteContext.Provider value={footnotes}>
            <KpiGrid
              grid={grid}
              mode={isGroup ? mode : "years"}
              entityYear={entityYear}
              yearsEntityId={yearsEntityId === "" ? null : yearsEntityId}
              search={search}
              categoryId={categoryId}
              collapsed={collapsed}
              onToggleBand={(id) =>
                setCollapsed((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
              onSaveCell={(args) => void useKpiStore.getState().saveCell(args)}
              onSaveCells={(items) => useKpiStore.getState().saveCells(items)}
              saveErrors={saveErrors}
              highlightCode={highlight}
              onRefresh={() => void useKpiStore.getState().refreshGrid()}
              notes={notes}
              breakdown={{
                isRecomputing,
                ...(canManageSets
                  ? {
                      onEditSet: () => void navigate(kpiSetManagementHref(clientId, setId)),
                      onAddBreakdownToSet: () =>
                        void navigate(kpiSetManagementHref(clientId, setId)),
                    }
                  : {}),
              }}
              formMismatches={formMismatches}
              onApplyFormValue={(m) =>
                void applyFormToKpi(m.projectId, m.questionCode).then(() =>
                  useKpiStore.getState().refreshGrid(),
                )
              }
            />
          </KpiFootnoteContext.Provider>
          <KpiFootnoteList numbering={footnotes} />
          <div
            className="flex justify-between text-xs px-3 py-2"
            style={{
              color: "var(--color-text-3)",
              borderTop: "1px solid var(--color-border)",
              flexShrink: 0,
            }}
          >
            <span>
              <b style={{ color: "var(--color-primary)" }}>
                {KPI_COPY.grid.footerOpen(
                  categories.length -
                    [...collapsed].filter((id) => categories.some((c) => c.id === id)).length,
                )}
              </b>{" "}
              ·{" "}
              {KPI_COPY.grid.footerIndicators(
                grid.indicators.filter((i) =>
                  isMetricIndicator(
                    i,
                    grid.indicators.filter((c) => c.parentId === i.id),
                  ),
                ).length,
              )}
            </span>
            <span>
              {KPI_COPY.grid.footerHelp}{" "}
              <b style={{ color: "var(--color-primary)" }}>{KPI_COPY.grid.footerFx}</b>{" "}
              {KPI_COPY.grid.footerFxHint}
            </span>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={staleConflict !== null}
        title={KPI_COPY.stale.title}
        body={`${KPI_COPY.stale.body}${staleValueText ? ` ${KPI_COPY.stale.serverValue(staleValueText)}` : ""}`}
        confirmLabel={KPI_COPY.stale.show}
        cancelLabel={KPI_COPY.stale.dismiss}
        tone="neutral"
        onConfirm={() => void useKpiStore.getState().acknowledgeStale(true)}
        onCancel={() => void useKpiStore.getState().acknowledgeStale(false)}
      />
    </KpiScreen>
  );
}
